// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;
let currentData;
let lastData;

const margins = {
  top: 20,
  right: 120,
  bottom: 25,
  left: 40,
};

let minValue;
let maxValue;

let minDomain;
let maxDomain;

let previousRecordDate;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

// Function to get current date in YYYY-MM-DD format
function getCurrentDate() {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

// Function to format AM/PM to a.m./p.m.
function formatAmPm(text) {
  return text.replace(/AM/g, 'a.m.').replace(/PM/g, 'p.m.');
}

// Function to format time in Eastern Time
function formatEasternTime(date) {
  // Check if it's after market close (4 PM ET) or before market open (9:30 AM ET)
  const now = new Date();
  const etTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const isAfterMarketClose = etTime.getHours() >= 16; // 4 PM or later
  const isBeforeMarketOpen = etTime.getHours() < 9 || (etTime.getHours() === 9 && etTime.getMinutes() < 30);

  if (isAfterMarketClose) {
    return 'Last close';
  } if (isBeforeMarketOpen) {
    return 'Pre-market';
  }

  const options = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  };

  const timeString = date.toLocaleTimeString('en-US', options);
  return formatAmPm(`${timeString} ET`);
}

// Function to format date and time for footnote
function formatFootnoteDateTime(date) {
  const options = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  };

  const dateTimeString = date.toLocaleString('en-US', options);
  return formatAmPm(`${dateTimeString} ET`);
}

// Function to update the footnote
function updateFootnote(date) {
  const footnoteElement = document.querySelector('.footnote');
  if (footnoteElement) {
    footnoteElement.textContent = `Last updated: ${formatFootnoteDateTime(date)}`;
  }
}

// Function to fetch and process market data
async function fetchMarketData() {
  const date = getCurrentDate();
  const response = await fetch(`https://production.dataviz.cnn.io/markets/index/DJII-USA,SP500-CME,COMP-USA/${date}`);
  const data = await response.json();

  // Find Nasdaq data
  const nasdaqData = data.find(item => item.symbol === 'COMP-USA');

  if (nasdaqData) {
    currentData = {
      date: new Date(nasdaqData.last_updated),
      close: nasdaqData.current_price,
      prevClose: nasdaqData.prev_close_price,
      change: nasdaqData.price_change_from_prev_close,
      percentChange: nasdaqData.percent_change_from_prev_close,
    };

    // Update chart if we have new data
    if (!lastData || currentData.date > lastData.date) {
      lastData = currentData;
      updateFootnote(currentData.date);
      draw();
    }
  }
}

// Initial data load
d3.json('https://ix.cnn.io/data/markets/nasdaq.json').then((response) => {
  rawData = response.data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    close: +d.close,
  }));

  // Filter to last 5 years
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

  chartData = rawData.filter(d => d.date > oneYearAgo);

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  const dataMin = d3.min(chartData, d => d.close);
  const dataMax = d3.max(chartData, d => d.close);
  const valueBuffer = (dataMax - dataMin) * 0.1;
  minValue = Math.floor((dataMin - valueBuffer) / 1000) * 1000;
  maxValue = Math.ceil((dataMax + valueBuffer) / 1000) * 1000;

  // Set initial lastData from historical data
  lastData = chartData[chartData.length - 1];

  // Start real-time updates
  fetchMarketData();
  setInterval(fetchMarketData, 30000); // Update every 30 seconds

  draw();
  window.onresize = draw;
});

function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 6 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(isMobile ? 3 : 6)
    .tickValues(isMobile ? [minDomain, maxDomain] : null)
    .tickFormat((d, i, nodes) => {
      if (isMobile) {
        return d3.timeFormat('%b %Y')(d);
      }
      // For desktop, show month and year for all ticks
      return d3.timeFormat('%b %Y')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickFormat(d3.format(','))
    .tickSize(-chartWidth);

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.close));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const line = g.append('g')
    .attr('class', 'line');

  // Create a combined dataset that includes both historical and current data
  const combinedData = currentData
    ? [...chartData, { date: currentData.date, close: currentData.close }]
    : chartData;

  line.append('path')
    .datum(combinedData)
    .attr('stroke', d => '#7c4ea5')
    .attr('stroke-width', '1.5')
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();

  // Add current data point if available
  if (currentData) {
    const currentPoint = g.append('g')
      .attr('class', 'current-point')
      .attr('transform', `translate(${[xScale(currentData.date), yScale(currentData.close)]})`);

    currentPoint.append('circle')
      .attr('r', '4')
      .attr('fill', '#7c4ea5')
      .attr('stroke', 'white')
      .attr('stroke-width', '2');

    const annotation = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${[xScale(currentData.date), yScale(currentData.close)]})`);

    // Price (bold)
    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-price')
      .text(d3.format(',')(currentData.close.toFixed(2)));

    // Change indicator (red/green)
    const changeColor = currentData.change >= 0 ? '#53A796' : '#c52622';
    const changeSymbol = currentData.change >= 0 ? '+' : '';

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-change')
      .attr('fill', changeColor)
      .text(`${changeSymbol}${d3.format('.2f')(currentData.change)} (${d3.format('.2%')(currentData.percentChange)})`)
      .attr('dy', '1.2em');

    // Time (lighter and italics)
    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-time')
      .text(formatEasternTime(currentData.date))
      .attr('dy', '2.4em');
  }

  window.pymChild.sendHeight();
}
